import React, { useEffect, useRef, useState } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { GOOGLE_API_KEY } from "../utils/env";
import { formatAddress, parseAddressComponent } from "../utils/helpers";
import { MAP_LIBRARIES } from "../Constants";

interface GoogleAutoCompleteTypes {
  type?: "formated" | "fullObject";
  address: string;
  placeHolder?: string;
  onChange: Function;
  onAddressPicked?: Function;
  // When true, tidy a free-typed address (spacing/casing) once the field loses
  // focus. Off by default so search inputs aren't reformatted while typing.
  formatOnBlur?: boolean;
  // When true, only an address selected from the Google Places suggestions is
  // accepted. Free-typed text that isn't selected is cleared on blur. Use for
  // forms that must store validated, consistently-formatted addresses.
  requireSelection?: boolean;
}

const GoogleAutoComplete: React.FC<GoogleAutoCompleteTypes> = ({
  type = "formated",
  address = "",
  placeHolder = "",
  onChange,
  onAddressPicked = null,
  formatOnBlur = false,
  requireSelection = false,
}) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  // The last address that was actually chosen from Google's suggestions, or a
  // saved value present when this field first mounted (editing an existing
  // venue). Free typing does NOT update this — so a partial entry like "177"
  // reverts to empty (or to the previously valid value) on blur.
  const selectedAddressRef = useRef<string>(address || "");
  // Remember whether the field mounted with a value already loaded async after
  // mount (e.g. the saved address arrives a tick later). Only the *first*
  // non-empty value the parent supplies is adopted, and only if the user hasn't
  // typed yet.
  const hasTypedRef = useRef(false);
  const adoptedInitialRef = useRef(!!address);

  useEffect(() => {
    if (!adoptedInitialRef.current && !hasTypedRef.current && address) {
      selectedAddressRef.current = address;
      adoptedInitialRef.current = true;
    }
  }, [address]);

  const handlePlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    // Ignore "selections" that aren't real places (no geometry/address).
    if (!place || (!place.formatted_address && !place.geometry)) return;

    const picked = place.formatted_address || "";
    selectedAddressRef.current = picked;
    adoptedInitialRef.current = true;

    if (type === "formated") {
      onChange(picked);
      if (onAddressPicked) onAddressPicked(picked);
    } else {
      const formated = parseAddressComponent(place?.address_components);
      onChange({
        address: picked,
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng(),
        ...formated,
      });
      if (onAddressPicked) onAddressPicked(picked);
    }
  };

  return (
    <>
      <Autocomplete onLoad={setAutocomplete} onPlaceChanged={handlePlaceChanged}>
        <input
          type="text"
          value={address}
          autoComplete="off"
          onKeyDown={(e) => {
            // Don't let Enter submit the form while choosing a suggestion.
            if (requireSelection && e.key === "Enter") {
              e.preventDefault();
            }
          }}
          onBlur={(e) => {
            const typed = e.target.value;

            if (requireSelection) {
              // Accept only addresses chosen from the suggestions. If what's in
              // the box isn't the selected place, revert to the last valid one
              // (empty if none) so unvalidated free text is never stored.
              if (typed !== selectedAddressRef.current) {
                const restore = selectedAddressRef.current || "";
                onChange(restore);
                if (onAddressPicked) onAddressPicked(restore);
              }
              return;
            }

            if (!formatOnBlur) return;
            const cleaned = formatAddress(typed);
            if (cleaned !== typed) {
              onChange(cleaned);
              if (onAddressPicked) onAddressPicked(cleaned);
            }
          }}
          onChange={(e) => {
            // The user is typing — never adopt typed text as the valid value.
            hasTypedRef.current = true;
            onChange(e.target.value);

            if (e.target.value === "" && onAddressPicked) {
              selectedAddressRef.current = "";
              setTimeout(() => {
                onAddressPicked("");
              }, 300);
            }
          }}
          placeholder={placeHolder || ""}
          className="form-control"
        />
      </Autocomplete>
    </>
  );
};

export default GoogleAutoComplete;
